import React, { useEffect, useState } from 'react';
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import {
  PublicClientApplication,
  EventType,
  type EventMessage,
  type AuthenticationResult,
  InteractionStatus,
} from '@azure/msal-browser';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { msalConfig, loginRequest } from './msalConfig';

/** Singleton MSAL instance — created once and reused. */
export const msalInstance = new PublicClientApplication(msalConfig);

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * AuthGate — renders children only when authenticated.
 * Automatically triggers redirect login when unauthenticated.
 */
const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, inProgress } = useMsal();

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      const accounts = instance.getAllAccounts();
      if (accounts.length === 0) {
        instance.loginRedirect(loginRequest);
      }
    }
  }, [instance, inProgress]);

  if (inProgress !== InteractionStatus.None) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Authenticating…
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            gap: 2,
          }}
        >
          <Typography variant="h6">Sign in to OptiBush</Typography>
          <Typography variant="body2" color="text.secondary">
            You need to authenticate with your Vibracoustic account to continue.
          </Typography>
          <Button
            variant="contained"
            onClick={() => instance.loginRedirect(loginRequest)}
          >
            Sign In
          </Button>
        </Box>
      </UnauthenticatedTemplate>
    </>
  );
};

/**
 * AuthProvider — initialises MSAL and wraps the app with MsalProvider + AuthGate.
 */
const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const init = async () => {
      await msalInstance.initialize();

      // Set the active account after redirect
      const response = await msalInstance.handleRedirectPromise();
      if (response?.account) {
        msalInstance.setActiveAccount(response.account);
      } else {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
        }
      }

      // Listen for login success to set active account
      msalInstance.addEventCallback((event: EventMessage) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          const payload = event.payload as AuthenticationResult;
          msalInstance.setActiveAccount(payload.account);
        }
      });

      setIsInitialized(true);
    };

    init();
  }, []);

  if (!isInitialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthGate>{children}</AuthGate>
    </MsalProvider>
  );
};

export default AuthProvider;
